import { useNavigate, useParams } from 'react-router-dom';
import SidePanel from '../SidePanel';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import findHomeSeekerApplicationById, { HomeseekerApplication } from '../../../../api/homeseeker-application/findHomeSeekerApplicationById';
import findScreeningRequest from '../../../../api/screening-request/findScreeningRequest';
import { SidePanelCustomEventType } from '../SidePanelEventManager';
import ContactInformation from './contact-information/ContactInformation';
import styles from './HomeseekerApplicationSidepanel.module.css';
import HighlightBlock from '../../../molecules/highlightBlock/HighlightBlock';
import Text from '../../../atoms/typography/Text';
import { FontSizeEnum } from '../../../enums';
import Divider from '../../../atoms/divider';
import CandidateSituationProfile from './situation-profile/CandidateSituationProfile';
import CandidateInteraction from './candidate-interaction/CandidateInteraction';
import Notifications from '../../notifications/Notifications';
import CandidateSidePanelButtonGroup from '../../button-group/CandidateSidePanelButtonGroup';
import MoveHomeseekerApplicationToWaitingList from '../../../../api/homeseeker/status/MoveHomeseekerApplicationToWaitingList';
import ApproveHomeseekerApplication from '../../../../api/homeseeker/status/ApproveHomeseekerApplication';
import SendMessageModal from '../../send-message-modal/SendMessageModal';
import RejectHomeseekerApplication from '../../../../api/homeseeker/status/RejectHomeseekerApplication';
import unassignTenantFromListing from '../../../../api/transactional-listing/unassignTenantFromListing';

enum ActionsEnum {
    reject = 'REJECT',
    approve = 'APPROVE',
    unassign = 'UNASSIGN',
    toWaitingList = 'TO_WAITING_LIST',
}

const HomeseekerApplicationSidePanel = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const { listingId, id, viewingId, candidateId } = useParams();

    const [screeningRequestId, setScreeningRequestId] = useState(null);
    const [isFetchingHomeseekerApplication, setIsFetchingHomeseekerApplication] = useState<boolean>(true);
    // TODO: Add screening to sidepanel.
    // eslint-disable-next-line
    const [isFetchingScreeningRequest, setIsFetchingScreeningRequest] = useState<boolean>(false);

    const [homeseekerApplication, setHomeseekerApplication] = useState<HomeseekerApplication | null>(null);
    const [transactionalListingId, setTransactionalListingId] = useState<string>(null);

    useEffect(() => {
        SidePanel.setOpen(!!candidateId);
        fetchHomeseekerApplication();
    }, [candidateId]);

    useEffect(() => {
        fetchScreeningRequest();
    }, [screeningRequestId]);

    useEffect(() => {
        if (id) {
            setTransactionalListingId(id);
        } else if (listingId) {
            setTransactionalListingId(listingId);
        }
    }, [id, listingId])

    const updateQueryParamsOnSidePanelChange = (e: SidePanelCustomEventType) => {
        if (!e.detail.open) {
            navigate(`/listing/${transactionalListingId}/viewings/${viewingId}/overview`);
        }
    }

    useEffect(() => {
        SidePanel.addListener(updateQueryParamsOnSidePanelChange);

        return () => {
            SidePanel.removeListener(updateQueryParamsOnSidePanelChange);
        }
    }, []);

    const fetchHomeseekerApplication = async () => {
        if (!candidateId) {
            return;
        }

        setIsFetchingHomeseekerApplication(true);

        try {
            const response = await findHomeSeekerApplicationById(candidateId);
            setHomeseekerApplication(response.result);
            if (response.result.screeningRequestId) {
                setScreeningRequestId(response.result.screeningRequestId);
            }
        } catch {
            Notifications.add({
                type: 'failed',
                message: t('api.request.failed')
            });
        } finally {
            setIsFetchingHomeseekerApplication(false);
        }
    }

    const fetchScreeningRequest = async () => {
        if (!screeningRequestId) {
            return;
        }

        setIsFetchingScreeningRequest(true);

        try {
            const response = await findScreeningRequest(screeningRequestId);
            console.log(response);
        } catch {
            Notifications.add({
                type: 'failed',
                message: t('api.request.failed')
            });
        } finally {
            setIsFetchingHomeseekerApplication(false);
        }
    }

    const getSalutation = (salutation) => {
        switch (salutation) {
            case 'FEMALE':
                return t('candidate.information.salutation.woman');
            case 'MALE':
                return t('candidate.information.salutation.man');
            default:
                return '';
        }
    }

    const updateHomeseekerApplication = async (action: ActionsEnum, notifyHomeseeker: boolean) => {
        switch (action) {
            case ActionsEnum.reject:
                await RejectHomeseekerApplication({ 
                    homeseekerApplicationIds: [homeseekerApplication.id], 
                    transactionalListingId: transactionalListingId, 
                    notifyHomeseeker
                });

                break;
            case ActionsEnum.approve:
                await ApproveHomeseekerApplication({ 
                    homeseekerApplicationIds: [homeseekerApplication.id]
                });

                break;
            case ActionsEnum.toWaitingList:
                await MoveHomeseekerApplicationToWaitingList({ 
                    homeseekerApplicationIds: [homeseekerApplication.id]
                });

                break;
            case ActionsEnum.unassign:
                await unassignTenantFromListing({
                    transactionalListingId,
                    homeseekerApplicationId: homeseekerApplication.id
                });

                break;
        }

        fetchHomeseekerApplication();
    }

    return (
        <SidePanel
            title={homeseekerApplication ? `${getSalutation(homeseekerApplication?.gender)} ${homeseekerApplication?.firstName}` : ''}
        >
            {!isFetchingHomeseekerApplication && <>
                <div className={styles.homeseekerApplicationSidePanel}>
                    <ContactInformation
                        birthday={homeseekerApplication.birthday}
                        email={homeseekerApplication.email}
                        phone={homeseekerApplication.phone}
                        employment={homeseekerApplication.employment}
                    />
                    <CandidateSidePanelButtonGroup
                        status={homeseekerApplication.status}
                        onMoveToWaitingList={() => updateHomeseekerApplication(ActionsEnum.toWaitingList, false)}
                        onUnassignTenant={() => updateHomeseekerApplication(ActionsEnum.unassign, false)}
                        onApprove={() => updateHomeseekerApplication(ActionsEnum.approve, false)}
                        onSendMessage={() => SendMessageModal.open(homeseekerApplication.id)}
                        onRejectWithoutMessage={() => updateHomeseekerApplication(ActionsEnum.reject, false)}
                        onRejectWithMessage={() => updateHomeseekerApplication(ActionsEnum.reject, true)}
                    />
                    <HighlightBlock
                        type='info'
                        bottomMarginInPx={16}
                    >
                        <Text
                            tag='p'
                            size={FontSizeEnum.Sm}
                        >
                            {t('candidate.popover.rejection.message')}
                        </Text>
                    </HighlightBlock>
                </div>
                <Divider />
                <div className={styles.situationProfile}>
                    <CandidateSituationProfile homeseeker={homeseekerApplication} />
                </div>
                <Divider />
                <CandidateInteraction homeseeker={homeseekerApplication} />
            </>}
        </SidePanel>
    )
}

export default HomeseekerApplicationSidePanel;
